// Vendor Libs
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { every, isEmpty, isFunction, filter, get } from 'lodash';

// Components
import Error from '../core/error';
import Loading from '../core/loading';
import SearchTotals from './search-totals';
import ServicesRefreshPanelAction from './services-refresh-panel-action';
import TableWithSort from '../core/table-with-sort';

// Contexts
import { RouteContext } from '../../contexts/route-context';
import { SearchContext } from '../../contexts/search-context';

// Configs
import errorConfig from '../../../config/error-config';

export default class SearchTableWithSort extends Component {
  componentDidMount() {
    this.goToFirstRecord();
  }

  componentWillUpdate(nextProps) {
    this.goToFirstRecord(nextProps);
  }

  getClass() {
    const { className, servicesDataKey } = this.props;

    return classnames('search-tablewithsort', `search-tablewithsort--${servicesDataKey}`, className);
  }

  getLinkFromFirstRow(props) {
    const { columnConfig, searchData } = props;
    const { location, routerParams } = this.context;
    const config = filter(columnConfig, { type: 'link' });

    if (config && config.length) {
      const { linkKey, link } = config[0];
      const linkKeys = typeof linkKey === 'string' ? [linkKey] : linkKey;
      const linkValue = linkKeys.map((key) => get(searchData, `0.${key}`));

      if (
        !isEmpty(linkValue.filter(Boolean))
                && location.pathname.startsWith(link)
                && every(linkKeys, (key) => !get(routerParams, key))
      ) {
        return `${link}/${linkValue.join('/')}`;
      }
    }
  }

  getTableWithSortProps = (searchContext) => {
    const {
      activeRowKeys,
      columnConfig,
      draggable,
      flexLayout,
      noSort,
      notSortable,
      onDragEnd,
      routerParams,
      rowDetails,
      searchData,
      sort,
      localSort,
    } = this.props;

    return {
      activeRowKeys,
      columnConfig,
      draggable,
      flexLayout,
      noSort,
      notSortable,
      onDragEnd,
      routerParams,
      rowDetails,
      searchData,
      sort,
      localSort,
      resetSort: () => this.resetSort(searchContext),
      setSort: (sortColumn) => this.setSort(sortColumn, searchContext),
    };
  };

  getSearchTotalsProps() {
    const { noPageLimit, number, numberOfElements, size, totalElements, totalPages, query } = this.props;

    return {
      noPageLimit,
      number,
      numberOfElements,
      size,
      totalElements,
      totalPages,
      query,
    };
  }

  setSort = (sortColumn, { setSort }) => {
    setSort(sortColumn);
  };

  handleReset = ({ resetServiceData }) => {
    const { onReset } = this.props;

    onReset ? onReset() : resetServiceData();
  };

  resetSort = ({ resetSort }) => {
    const { sort } = this.props;

    resetSort(!!sort.length, true);
  };

  goToFirstRecord(props = this.props) {
    const { push } = this.context;
    const url = this.getLinkFromFirstRow(props);

    if (url) {
      push(url);
    }
  }

  renderError(searchContext) {
    const { error, fetching, noRecordsChildren, noRecordsError, searchData, noReset } = this.props;
    const errorMessage =            !error && !fetching && !searchData.length
              ? noRecordsError || get(errorConfig, 'no_matching_criteria.message')
              : error;
    const props = {
      error: errorMessage,
      onReset: noReset ? undefined : () => this.handleReset(searchContext),
    };
    const getNoRecordsChildren = () => (!searchData.length && !fetching
                ? isFunction(noRecordsChildren)
                    ? noRecordsChildren()
                    : noRecordsChildren
                : null);

    return (
            <>
                <Error {...props} />
                {getNoRecordsChildren()}
            </>
    );
  }

  renderTableWithSort(searchContext) {
    const { searchData } = this.props;

    return searchData.length ? <TableWithSort {...this.getTableWithSortProps(searchContext)} /> : null;
  }

  render() {
    const { fetching, noRefresh } = this.props;

    return (
            <div className={this.getClass()}>
                {!noRefresh ? <ServicesRefreshPanelAction /> : null}
                <Loading loading={fetching} />
                <SearchContext.Consumer>
                    {(searchContext) => (
                        <>
                            {this.renderError(searchContext)}
                            {this.renderTableWithSort(searchContext)}
                            <SearchTotals {...this.getSearchTotalsProps()} />
                        </>
                    )}
                </SearchContext.Consumer>
            </div>
    );
  }
}

SearchTableWithSort.propTypes = {
  activeRowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  activeRowKeys: PropTypes.array,
  activeRowValue: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  className: PropTypes.string,
  columnConfig: PropTypes.arrayOf(PropTypes.object).isRequired,
  draggable: PropTypes.bool,
  error: PropTypes.string,
  fetching: PropTypes.bool,
  flexLayout: PropTypes.bool,
  localSort: PropTypes.bool,
  noPageLimit: PropTypes.bool,
  noRecordsChildren: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  noRecordsError: PropTypes.string,
  noRefresh: PropTypes.bool,
  noReset: PropTypes.bool,
  noSort: PropTypes.bool,
  notSortable: PropTypes.array,
  number: PropTypes.number,
  numberOfElements: PropTypes.number,
  onDragEnd: PropTypes.func,
  onReset: PropTypes.func,
  query: PropTypes.object,
  routerParams: PropTypes.object,
  rowDetailComponent: PropTypes.node,
  rowDetailKeys: PropTypes.arrayOf(PropTypes.string),
  rowDetails: PropTypes.object,
  searchData: PropTypes.array.isRequired,
  selectFirstRecordOnPage: PropTypes.string,
  servicesDataKey: PropTypes.string,
  size: PropTypes.number,
  sort: PropTypes.array.isRequired,
  totalElements: PropTypes.number,
  totalPages: PropTypes.number.isRequired,
};

SearchTableWithSort.contextType = RouteContext;
